import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Package, Cylinder, Flame, Wrench, Box, Boxes } from 'lucide-react';
import type { ProductType, ProductTypeFormData } from '@/types/productType';

interface ProductTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productType?: ProductType | null;
  onSave: (data: ProductTypeFormData) => Promise<boolean>;
  isLoading?: boolean;
}

// TIPO-CODIGO-UX (Diretor 31/10, I.178): o código interno do tipo é campo do sistema e não aparece nesta janela — é
// gerado a partir do nome na criação (useProductTypes) e fica fixo depois; o comportamento do tipo é só pelos toggles.

const iconOptions = [
  { value: 'Package', label: 'Pacote', icon: Package },
  { value: 'Cylinder', label: 'Cilindro', icon: Cylinder },
  { value: 'Flame', label: 'Chama', icon: Flame },
  { value: 'Wrench', label: 'Ferramenta', icon: Wrench },
  { value: 'Box', label: 'Caixa', icon: Box },
  { value: 'Boxes', label: 'Caixas', icon: Boxes },
];

export const ProductTypeDialog = ({
  open,
  onOpenChange,
  productType,
  onSave,
  isLoading = false,
}: ProductTypeDialogProps) => {
  const [formData, setFormData] = useState<ProductTypeFormData>({
    name: '',
    description: '',
    allows_rental: false,
    allows_sale: true,
    requires_serial_tracking: false,
    allows_manufacturing: false,
    allows_variation: true,
    icon: 'Package',
  });

  useEffect(() => {
    if (productType) {
      setFormData({
        name: productType.name,
        description: productType.description || '',
        allows_rental: productType.allows_rental,
        allows_sale: productType.allows_sale,
        requires_serial_tracking: productType.requires_serial_tracking,
        allows_manufacturing: productType.allows_manufacturing,
        allows_variation: productType.allows_variation !== false,
        icon: productType.icon || 'Package',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        allows_rental: false,
        allows_sale: true,
        requires_serial_tracking: false,
        allows_manufacturing: false,
        allows_variation: true,
        icon: 'Package',
      });
    }
  }, [productType, open]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    const success = await onSave(formData);
    if (success) {
      onOpenChange(false);
    }
  };

  const SelectedIcon = iconOptions.find(i => i.value === formData.icon)?.icon || Package;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {productType ? 'Editar Tipo de Produto' : 'Novo Tipo de Produto'}
          </DialogTitle>
          <DialogDescription>
            Configure as propriedades deste tipo de produto
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Cilindro, Gás, Equipamento..."
            />
          </div>

          <div>
            <div className="space-y-2">
              <Label>Ícone</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <SelectedIcon className="h-4 w-4" />
                      <span>{iconOptions.find(i => i.value === formData.icon)?.label}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="h-4 w-4" />
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrição opcional do tipo de produto..."
              rows={2}
            />
          </div>

          <div className="space-y-3 border rounded-lg p-4">
            <h4 className="font-medium text-sm">Configurações</h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="allows_sale">Permite Venda</Label>
                <p className="text-xs text-muted-foreground">
                  Produtos deste tipo podem ser vendidos
                </p>
              </div>
              <Switch
                id="allows_sale"
                checked={formData.allows_sale}
                onCheckedChange={(checked) => setFormData({ ...formData, allows_sale: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="allows_rental">Permite Locação</Label>
                <p className="text-xs text-muted-foreground">
                  Produtos deste tipo podem ser locados
                </p>
              </div>
              <Switch
                id="allows_rental"
                checked={formData.allows_rental}
                onCheckedChange={(checked) => setFormData({ ...formData, allows_rental: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="requires_serial_tracking">Rastreamento Serial</Label>
                <p className="text-xs text-muted-foreground">
                  Exige número de série para cada item
                </p>
              </div>
              <Switch
                id="requires_serial_tracking"
                checked={formData.requires_serial_tracking}
                onCheckedChange={(checked) => setFormData({ ...formData, requires_serial_tracking: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="allows_manufacturing">Permite Produção</Label>
                <p className="text-xs text-muted-foreground">
                  Produtos deste tipo aparecem em Fichas Técnicas
                </p>
              </div>
              <Switch
                id="allows_manufacturing"
                checked={formData.allows_manufacturing || false}
                onCheckedChange={(checked) => setFormData({ ...formData, allows_manufacturing: checked })}
              />
            </div>

            {/* GRADE-UX parte 3, ajustes (I.178, D6): trava do "Produto com variação" por tipo e por empresa */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="allows_variation">Permite variação</Label>
                <p className="text-xs text-muted-foreground">
                  Libera o "Produto com variação" (cor, tamanho...) no Novo Produto
                </p>
              </div>
              <Switch
                id="allows_variation"
                checked={formData.allows_variation !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, allows_variation: checked })}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !formData.name.trim()}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {productType ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
